import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import samhealthLogo from '../../assets/samhealth.png';

// Professional, official-letter style KYP PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    paddingTop: 20,
    paddingHorizontal: 20,
    paddingBottom: 20,
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.4,
  },
  frame: {
    borderWidth: 1.5,
    borderColor: '#1e4d72',
    borderStyle: 'solid',
    borderRadius: 6,
    paddingTop: 16,
    paddingHorizontal: 20,
    paddingBottom: 16,
    minHeight: '100%',
  },
  header: {
    borderBottomWidth: 1,
    borderBottomColor: '#C7CDD3',
    borderBottomStyle: 'solid',
    paddingBottom: 10,
    marginBottom: 14,
  },
  orgName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  orgSub: {
    fontSize: 9,
    color: '#4B5563',
    marginTop: 2,
  },
  orgContact: {
    fontSize: 8,
    color: '#6B7280',
    marginTop: 2,
  },
  title: {
    marginTop: 12,
    fontSize: 12,
    color: '#111827',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  metaRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  metaItem: {
    fontSize: 9,
    color: '#374151',
  },
  // Letter blocks
  letterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 12,
  },
  letterCol: {
    width: '48%',
  },
  toLabel: {
    fontSize: 8,
    color: '#6B7280',
    marginBottom: 2,
  },
  toValue: {
    fontSize: 10,
    color: '#111827',
  },
  subject: {
    marginTop: 14,
    fontSize: 11,
    color: '#111827',
    fontWeight: 'bold',
  },
  salutation: {
    marginTop: 12,
    fontSize: 10,
    color: '#111827',
  },
  paragraph: {
    marginTop: 8,
    fontSize: 10,
    color: '#111827',
    lineHeight: 1.6,
    textAlign: 'justify',
  },
  section: {
    marginTop: 12,
  },
  sectionHeading: {
    fontSize: 10,
    color: '#111827',
    fontWeight: 'bold',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    borderBottomStyle: 'solid',
    paddingBottom: 6,
    marginBottom: 6,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  cell: {
    width: '50%',
    paddingRight: 8,
    marginBottom: 6,
  },
  label: {
    fontSize: 8,
    color: '#6B7280',
    textTransform: 'uppercase',
    marginBottom: 2,
  },
  value: {
    fontSize: 10,
    color: '#111827',
  },
  note: {
    fontSize: 9,
    color: '#374151',
    marginTop: 4,
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    borderTopStyle: 'solid',
    marginTop: 10,
    marginBottom: 10,
  },
  terms: {
    fontSize: 8.5,
    color: '#374151',
    lineHeight: 1.5,
    marginTop: 6,
  },
  footerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 18,
  },
  closing: {
    marginTop: 18,
    fontSize: 10,
    color: '#111827',
  },
  signBlock: {
    width: '32%',
  },
  signLine: {
    borderTopWidth: 1,
    borderTopColor: '#9CA3AF',
    borderTopStyle: 'solid',
    marginTop: 22,
    paddingTop: 4,
  },
  signLabel: {
    fontSize: 8,
    color: '#6B7280',
    textAlign: 'center',
  },
  footer: {
    marginTop: 'auto',
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    borderTopStyle: 'solid',
    paddingTop: 8,
  },
  footerText: {
    fontSize: 8,
    color: '#6B7280',
    textAlign: 'center',
  },
  // Extra sections adapted from comprehensive/legacy templates
  tag: {
    paddingVertical: 3,
    paddingHorizontal: 6,
    backgroundColor: '#F3F4F6',
    color: '#374151',
    borderRadius: 4,
    fontSize: 9,
    alignSelf: 'flex-start',
    marginTop: 4,
  },
  listItem: {
    fontSize: 9,
    color: '#111827',
    marginBottom: 4,
  },

  // Simple table styles
  table: {
    width: '100%',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderStyle: 'solid',
    marginTop: 6,
  },
  tableRow: {
    flexDirection: 'row',
  },
  tdLabel: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    color: '#6B7280',
    borderRightWidth: 1,
    borderRightColor: '#E5E7EB',
    borderRightStyle: 'solid',
  },
  tdValue: {
    flex: 1,
    padding: 6,
    fontSize: 9,
    color: '#111827',
    fontWeight: 'bold',
    textAlign: 'right',
  },
});

// Render a field only if value is meaningful (hide empties/placeholders)
const normalizeText = (str) => {
  if (typeof str !== 'string') return str;
  let s = str.replace(/\s+/g, ' ').trim();
  const fullRep = s.match(/^(.+?)\1+$/);
  if (fullRep) s = fullRep[1].trim();
  return s;
};

const field = (label, rawValue) => {
  const isEmpty = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'number') return v === 0; // treat 0 as empty for policy amounts
    if (typeof v === 'string') {
      const t = v.trim();
      if (!t) return true;
      const upper = t.toUpperCase();
      // common placeholders
      if (['N/A', '--', '—', '₹ 0', '₹ 0.00'].includes(t)) return true;
      if (upper.includes('N/A') || t.includes('—')) return true;
      // strings like just separators
      if (/^[-/_,\s]+$/.test(t)) return true;
    }
    return false;
  };

  if (isEmpty(rawValue)) return null;

  return (
    <View style={styles.cell}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{normalizeText(String(rawValue))}</Text>
    </View>
  );
};

const ProfessionalKYPDocument = ({ application, kypData, leadGeneratorData }) => {
  const app = application || {};
  const kyp = kypData || {};
  const det = kyp.applicationDetails || {};

  const lg = leadGeneratorData || kyp.leadGenerator || {};
  const orgName = (lg.entityName || lg.companyName || 'Samriddhi Healthcare').toUpperCase();
  const orgSub = lg.description || 'Medical Review and Insurance Facilitation';
  const orgCity = lg.city || 'India';

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-GB');
  const validUntil = kyp.validityPeriod
    ? (() => { try { const n = parseInt(String(kyp.validityPeriod).match(/\d+/)?.[0] || '90', 10); return new Date(Date.now() + n*24*60*60*1000).toLocaleDateString('en-GB'); } catch { return dateStr; } })()
    : new Date(Date.now() + 90*24*60*60*1000).toLocaleDateString('en-GB');

  const amount = (v) => `Rs. ${(Number(v||0)).toLocaleString('en-IN')}`;

  // Patient Name should come strictly from PATIENT fields (not policyholder)
  const getPatientDisplayName = (app, det) => {
    const candidates = [
      det.patientName,
      app?.patient?.fullName,
      app?.patient?.originalName,
      app?.patient?.completeName,
      app?.patient?.displayName,
      app?.patient?.legalName,
      app?.patient?.name,
    ].filter(v => typeof v === 'string' && v.trim().length > 0);
    if (!candidates.length) return 'Patient';
    const longest = candidates.reduce((a,b) => (b.trim().length > a.trim().length ? b : a));
    return normalizeText(longest.replace(/\s+/g,' ').trim());
  };
  const patientDisplayName = getPatientDisplayName(app, det);

  // Build co-pay/deductible summary
  const coPayDeductible = (() => {
    const cs = kyp?.coPayStructure || kyp?.policyDetails?.coPayStructure || {};
    const ds = kyp?.deductiblesAndSublimits || kyp?.policyDetails?.deductiblesAndSublimits || {};
    const parts = [];
    const fmt = (val, type) => {
      if (val === undefined || val === null || val === '' || Number.isNaN(Number(val))) return '';
      const n = Number(val);
      if (type && String(type).toLowerCase().includes('percentage')) return `${n}%`;
      return `₹${n.toLocaleString('en-IN')}`;
    };
    const pushIf = (label, value, type) => {
      const fv = fmt(value, type);
      if (fv) parts.push(`${label}: ${fv}`);
    };
    if (cs.generalCoPay !== undefined) pushIf('General Co-pay', cs.generalCoPay, cs.coPayType);
    if (cs.generalCoPayApplicable) pushIf('General Co-pay', cs.generalCoPayValue, cs.generalCoPayType);
    if (cs.parentalCoPayApplicable) pushIf('Parental Co-pay', cs.parentalCoPayValue, cs.parentalCoPayType);
    if (cs.locationBasedCoPayApplicable) {
      if (cs.metroCoPayValue !== undefined) pushIf('Metro Co-pay', cs.metroCoPayValue, cs.metroCoPayType);
      if (cs.nonMetroCoPayValue !== undefined) pushIf('Non-Metro Co-pay', cs.nonMetroCoPayValue, cs.nonMetroCoPayType);
    }
    if (cs.mandatoryCoPayApplicable) pushIf('Mandatory Co-pay', cs.mandatoryCoPayValue, cs.mandatoryCoPayType);
    if (cs.nonNetworkCoPayApplicable) pushIf('Non-Network Co-pay', cs.nonNetworkCoPayValue, cs.nonNetworkCoPayType);
    if (cs.voluntaryCoPayApplicable) pushIf('Voluntary Co-pay', cs.voluntaryCoPayValue, cs.voluntaryCoPayType);
    if (cs.nonIntimationCoPayApplicable) pushIf('Non-intimation Co-pay', cs.nonIntimationCoPayValue, cs.nonIntimationCoPayType);
    if (ds.compulsoryDeductible !== undefined && Number(ds.compulsoryDeductible) > 0) parts.push(`Deductible: ₹${Number(ds.compulsoryDeductible).toLocaleString('en-IN')}`);
    return parts.join(' | ');
  })();

  // Effective verification flags: default to Yes when KYP is generated or MV approved
  const mvw = app?.workflow?.medicalVerification || {};
  const effectiveMedicalHistoryVerified =
    (kyp.medicalHistoryVerified !== undefined)
      ? Boolean(kyp.medicalHistoryVerified)
      : Boolean(mvw.status === 'approved' || mvw.kypGenerated === true);
  const effectiveDocumentsAuthentic =
    (kyp.documentsAuthentic !== undefined)
      ? Boolean(kyp.documentsAuthentic)
      : Boolean(mvw.status === 'approved' || mvw.kypGenerated === true);
  const effectiveTreatmentAppropriate =
    (kyp.treatmentAppropriate !== undefined)
      ? Boolean(kyp.treatmentAppropriate)
      : Boolean(mvw.status === 'approved' || mvw.kypGenerated === true);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.frame}>
        {/* Header / Letterhead */}
        <View style={styles.header}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            {/* Left: SamHealth logo */}
            <View style={{ width: 100, height: 56, borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'solid', borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }}>
              <Image src={samhealthLogo} style={{ width: 96, height: 52 }} />
            </View>
            {/* Right: Lead generator logo if available */}
            {lg?.documents?.entityLogo?.url && (
              <View style={{ width: 100, height: 56, borderWidth: 1, borderColor: '#E5E7EB', borderStyle: 'solid', borderRadius: 4, overflow: 'hidden', alignItems: 'center', justifyContent: 'center' }}>
                <Image src={lg.documents.entityLogo.url} style={{ width: 96, height: 52 }} />
              </View>
            )}
          </View>
          <View style={{ marginTop: 8 }}>
            <Text style={styles.orgName}>{orgName}</Text>
            <Text style={styles.orgSub}>{orgSub}</Text>
            <Text style={styles.orgContact}>📍 {orgCity} • ✉️ info@samriddhihealthcare.com • ☎️ +91-XXXXXXXXXX</Text>
          </View>
          <Text style={styles.title}>Know Your Patient (KYP) Authorization Letter</Text>
          <View style={styles.metaRow}>
            <Text style={styles.metaItem}>Case ID: {app.caseId || app.applicationId || det.applicationId}</Text>
            <Text style={styles.metaItem}>Date: {dateStr}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaItem}>Application ID: {app.applicationId || det.applicationId || 'N/A'}</Text>
            <Text style={styles.metaItem}>Intimation ID: {det.intimationId || 'N/A'}</Text>
          </View>
          <View style={styles.metaRow}>
            <Text style={styles.metaItem}>Valid Until: {validUntil}</Text>
          </View>
        </View>

        {/* Letter addressing */}
        <View style={styles.letterRow}>
          <View style={styles.letterCol}>
            <Text style={styles.toLabel}>To</Text>
            <Text style={styles.toValue}>{det.hospitalName || app.hospitalName || 'Hospital'}</Text>
            {(det.city || app.hospital?.city) && (
              <Text style={styles.toValue}>{det.city || app.hospital?.city}</Text>
            )}
          </View>
          <View style={styles.letterCol}>
            <Text style={styles.toLabel}>From</Text>
            <Text style={styles.toValue}>{orgName}</Text>
            <Text style={styles.toValue}>{orgCity}</Text>
          </View>
        </View>

        {/* Subject and salutation */}
        <Text style={styles.subject}>
          Subject: KYP Authorization for {patientDisplayName} (Application ID: {app.applicationId || det.applicationId || 'N/A'})
        </Text>
        <Text style={styles.salutation}>Dear Sir/Madam,</Text>
        <Text style={styles.paragraph}>
          We write to confirm that the enclosed details have been reviewed by our medical officer and the case qualifies for provisional authorization as per the insurance policy terms. The salient particulars of the patient, hospitalization and coverage are summarized below for your official reference.
        </Text>

        {/* Patient & Hospital */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Patient & Hospital</Text>
          <View style={styles.grid}>
            {field('Patient Name', patientDisplayName)}
            {field('Age / Gender', `${app.patient?.age || ''} / ${(app.patient?.gender || '').toString().toUpperCase()}`)}
            {field('Hospital', det.hospitalName || app.hospitalName)}
            {field('City', det.city || app.hospital?.city)}
          </View>
        </View>

        {/* Medical Details */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Medical Details</Text>
          <View style={styles.grid}>
            {field('Diagnosis', det.diagnosis || app.medical?.diagnosis)}
            {field('Procedure', det.procedureName || app.medical?.procedureName)}
            {field('Treatment Type', det.treatmentType || app.medical?.treatmentType)}
            {field('Admission Date', det.admissionDate)}
          </View>
          {!!det.roomEligibility && (
            <Text style={styles.note}>Room Eligibility: {det.roomEligibility} {det.roomAmountPerDay ? `| Room: ${amount(det.roomAmountPerDay)}/day` : ''} {det.icuAmountPerDay ? `| ICU: ${amount(det.icuAmountPerDay)}/day` : ''}</Text>
          )}
        </View>

        {/* Policy Details */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Policy Details</Text>
          <View style={styles.grid}>
            {field('Insurer', det.insuranceCompany || app.policy?.insuranceCompany)}
            {field('Policy Number', det.policyNumber || app.policy?.number)}
            {field('Policy Period', det.policyPeriod || ((app.policy?.startDate && app.policy?.endDate) ? `${app.policy?.startDate} to ${app.policy?.endDate}` : ''))}
            {field('Primary Insured', det.primaryInsuredName || app.policy?.primaryInsuredName || det.patientName || app.patient?.name)}
            {field('Sum Insured (Base)', amount(det.sumInsured || app.policy?.coverageAmount))}
            {field('Cumulative Bonus', amount(det.cumulativeBonus || 0))}
            {field('Top-up SI', amount(det.topupSI || 0))}
            {field('Effective Coverage', amount(det.effectiveCoverage || (Number(app.policy?.coverageAmount||0) + Number(app.policy?.cumulativeBonus||0) + Number(app.policy?.topupSI||0))))}
            {field('Balance Sum Insured', amount(det.balanceSumInsured || app.policy?.balanceSumInsured || app.policy?.coverageAmount))}
          </View>
        </View>

        {/* Authorization Summary */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Authorization Summary</Text>
          <View style={styles.grid}>
            {field('Medical History Verified', effectiveMedicalHistoryVerified ? 'Yes' : 'No')}
            {field('Documents Authentic', effectiveDocumentsAuthentic ? 'Yes' : 'No')}
            {field('Treatment Appropriate', effectiveTreatmentAppropriate ? 'Yes' : 'Under Review')}
            {coPayDeductible && field('Co-Pay / Deductible', coPayDeductible)}
            {field('Sub-Limit', kyp.subLimit || 'As per policy')}
            {field('Waiting Periods', `${kyp.waitingPeriod || '30 Days'} | Specific: ${kyp.specificWaiting || '2 Years'} | PED: ${kyp.pedWaiting || '4 Years'}`)}
            {field('Estimated Cost', amount(det.estimatedCost || app.medical?.estimatedCost))}
            {field('Disbursement Approved', `${amount(kyp.approvedDisbursementAmount || 0)}${kyp.disbursementPercentage ? ` (${kyp.disbursementPercentage}%)` : ''}`)}
            {field('Pre-existing Conditions', kyp.preExistingConditions || 'As declared')}
          </View>
          {!!kyp.clinicalNotes && (
            <Text style={styles.note}>Clinical Notes: {kyp.clinicalNotes}</Text>
          )}
          {!!kyp.recommendation && (
            <Text style={styles.note}>Recommendation: {kyp.recommendation}</Text>
          )}
        </View>

        {/* Coverage Breakdown (tabular) */}
        {(kyp.coverageBreakdown || kyp.policyDetails) && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Coverage Breakdown & Eligibility</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Base Coverage</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.baseCoverage || kyp.policyDetails?.baseSumInsured || det.sumInsured)}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Cumulative Bonus</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.cumulativeBonus || kyp.policyDetails?.cumulativeBonus || det.cumulativeBonus)}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Top-up Coverage</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.topUpCoverage || kyp.policyDetails?.topUpSumInsured || kyp.topupSI)}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Effective Total Sum Insured</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.effectiveTotalSumInsured || kyp.policyDetails?.effectiveTotalSumInsured || det.effectiveCoverage)}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Utilized Current Year</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.utilized || kyp.policyDetails?.sumInsuredUtilizedCurrentYear || 0)}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Balance Sum Insured</Text>
                <Text style={styles.tdValue}>{amount(kyp.coverageBreakdown?.balanceSumInsured || kyp.policyDetails?.balanceSumInsured || det.balanceSumInsured)}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Room & ICU Eligibility (tabular) */}
        {(det.roomEligibility || det.roomAmountPerDay || det.icuAmountPerDay) && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Room & ICU Eligibility</Text>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Eligible Room</Text>
                <Text style={styles.tdValue}>{det.roomEligibility || 'As per policy'}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>Room Amount/Day</Text>
                <Text style={styles.tdValue}>{det.roomAmountPerDay ? amount(det.roomAmountPerDay) : 'As per policy'}</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tdLabel}>ICU Amount/Day</Text>
                <Text style={styles.tdValue}>{det.icuAmountPerDay ? amount(det.icuAmountPerDay) : 'As per policy'}</Text>
              </View>
            </View>
          </View>
        )}

        {/* Co-pay Structure */}
        {kyp.policyDetails?.coPayStructure && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Co-Pay Structure</Text>
            <View style={styles.grid}>
              {kyp.policyDetails.coPayStructure.generalCoPayApplicable && field('General Co-pay', `${kyp.policyDetails.coPayStructure.generalCoPayValue}${String(kyp.policyDetails.coPayStructure.generalCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.parentalCoPayApplicable && field('Parental Co-pay', `${kyp.policyDetails.coPayStructure.parentalCoPayValue}${String(kyp.policyDetails.coPayStructure.parentalCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.locationBasedCoPayApplicable && field('Metro Co-pay', `${kyp.policyDetails.coPayStructure.metroCoPayValue}${String(kyp.policyDetails.coPayStructure.metroCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.locationBasedCoPayApplicable && field('Non-Metro Co-pay', `${kyp.policyDetails.coPayStructure.nonMetroCoPayValue}${String(kyp.policyDetails.coPayStructure.nonMetroCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.mandatoryCoPayApplicable && field('Mandatory Co-pay', `${kyp.policyDetails.coPayStructure.mandatoryCoPayValue}${String(kyp.policyDetails.coPayStructure.mandatoryCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.nonNetworkCoPayApplicable && field('Non-Network Hospital Co-pay', `${kyp.policyDetails.coPayStructure.nonNetworkCoPayValue}${String(kyp.policyDetails.coPayStructure.nonNetworkCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.voluntaryCoPayApplicable && field('Voluntary Co-pay', `${kyp.policyDetails.coPayStructure.voluntaryCoPayValue}${String(kyp.policyDetails.coPayStructure.voluntaryCoPayType||'').includes('percentage') ? '%' : ''}`)}
              {kyp.policyDetails.coPayStructure.nonIntimationCoPayApplicable && field('Non-intimation Co-pay', `${kyp.policyDetails.coPayStructure.nonIntimationCoPayValue}${String(kyp.policyDetails.coPayStructure.nonIntimationCoPayType||'').includes('percentage') ? '%' : ''}`)}
            </View>
          </View>
        )}

        {/* Deductibles & Sublimits */}
        {kyp.policyDetails?.deductiblesAndSublimits && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Deductibles & Sublimits</Text>
            <View style={styles.grid}>
              {kyp.policyDetails.deductiblesAndSublimits.compulsoryDeductible !== undefined && field('Compulsory Deductible', amount(kyp.policyDetails.deductiblesAndSublimits.compulsoryDeductible))}
              {kyp.policyDetails.deductiblesAndSublimits.prePostNatalLimit && field('Pre/Post Natal Limit', amount(kyp.policyDetails.deductiblesAndSublimits.prePostNatalLimit))}
            </View>
            {(kyp.policyDetails.deductiblesAndSublimits.ailmentProcedureSublimits || []).length > 0 && (
              <View style={{ marginTop: 6 }}>
                <Text style={styles.note}>Procedure Sublimits:</Text>
                {kyp.policyDetails.deductiblesAndSublimits.ailmentProcedureSublimits.map((s, idx) => (
                  <Text key={idx} style={styles.listItem}>• {s.ailmentProcedure || 'Procedure'}: {amount(s.sublimitAmount || 0)}</Text>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Intimation Details */}
        {kyp.intimationDetails && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Intimation Details</Text>
            <View style={styles.grid}>
              {kyp.intimationDetails.intimationDone !== undefined && field('Intimation Done', kyp.intimationDetails.intimationDone ? 'Yes' : 'No')}
              {kyp.intimationDetails.intimationId && field('Intimation ID', kyp.intimationDetails.intimationId)}
              {kyp.intimationDetails.intimationSource && field('Intimation Source', kyp.intimationDetails.intimationSource)}
            </View>
          </View>
        )}

        {/* Policy Analysis */}
        {kyp.policyAnalysis && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Policy Analysis</Text>
            <View style={styles.grid}>
              {kyp.policyAnalysis.policyValid !== undefined && field('Policy Valid', kyp.policyAnalysis.policyValid ? 'Yes' : 'No')}
              {kyp.policyAnalysis.coverageActive !== undefined && field('Coverage Active', kyp.policyAnalysis.coverageActive ? 'Yes' : 'No')}
              {kyp.policyAnalysis.beneficiaryEligible !== undefined && field('Beneficiary Eligible', kyp.policyAnalysis.beneficiaryEligible ? 'Yes' : 'No')}
              {kyp.policyAnalysis.treatmentCovered !== undefined && field('Treatment Covered', kyp.policyAnalysis.treatmentCovered ? 'Yes' : 'No')}
              {kyp.policyAnalysis.preAuthRequired !== undefined && field('Pre-Auth Required', kyp.policyAnalysis.preAuthRequired ? 'Yes' : 'No')}
              {kyp.policyAnalysis.preAuthObtained !== undefined && field('Pre-Auth Obtained', kyp.policyAnalysis.preAuthObtained ? 'Yes' : 'No')}
              {kyp.policyAnalysis.claimLimitAvailable !== undefined && field('Claim Limit Available', kyp.policyAnalysis.claimLimitAvailable ? 'Yes' : 'No')}
              {kyp.policyAnalysis.waitingPeriodCompleted !== undefined && field('Waiting Period Completed', kyp.policyAnalysis.waitingPeriodCompleted ? 'Yes' : 'No')}
            </View>
          </View>
        )}

        {/* Claim History */}
        {kyp.claimHistory && (
          <View style={styles.section}>
            <Text style={styles.sectionHeading}>Claim History (Current Year)</Text>
            <View style={styles.grid}>
              {Array.isArray(kyp.claimHistory.previousClaimsCurrentYear) && kyp.claimHistory.previousClaimsCurrentYear.length > 0 && (
                <View style={{ width: '100%' }}>
                  {kyp.claimHistory.previousClaimsCurrentYear.map((c, idx) => (
                    <Text key={idx} style={styles.listItem}>• {c?.date || '—'} | {c?.description || 'Claim'} | {amount(c?.amount || 0)}</Text>
                  ))}
                </View>
              )}
              {kyp.claimHistory.totalClaimedCurrentYear && field('Total Claimed (CY)', amount(kyp.claimHistory.totalClaimedCurrentYear))}
            </View>
          </View>
        )}

        {/* Special Remarks (external-facing: ONLY letter remarks) */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Special Remarks</Text>
          <Text style={styles.paragraph}>
            {kyp.letterRemarks && String(kyp.letterRemarks).trim() ? kyp.letterRemarks : 'No special remarks'}
          </Text>
        </View>

        {/* Instructions */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Instructions</Text>
          <Text style={styles.terms}>
            The case is admissible based on documents shared at initial assessment. Final liability will be determined on receipt of final bill, discharge summary and relevant diagnostic reports. Room category upgrades may attract proportionate deductions. Expenses not payable as per policy terms (e.g., admission fee, documentation, diet, consumables) will be excluded.
          </Text>
        </View>

        <View style={styles.divider} />

  {/* Terms (concise, professional) */}
        <View style={styles.section}>
          <Text style={styles.sectionHeading}>Terms & Conditions (Summary)</Text>
          <Text style={styles.terms}>
            This authorization is governed by the insurance policy terms, applicable sub-limits, and deductibles. Any upgrade in room category may result in proportionate deductions. Claims are subject to receipt and verification of complete discharge documentation. Misrepresentation or concealment of facts may render this authorization void. This letter is valid for {kyp.validityPeriod || '15 days'} from the date of issue.
          </Text>
        </View>

  {/* Closing */}
  <Text style={styles.closing}>Yours faithfully,</Text>

        {/* Signatures */}
        <View style={styles.footerRow}>
          <View style={styles.signBlock}>
            <View style={styles.signLine} />
            <Text style={styles.signLabel}>Authorized Signatory</Text>
          </View>
          <View style={styles.signBlock}>
            <View style={styles.signLine} />
            <Text style={styles.signLabel}>Hospital Representative</Text>
          </View>
          <View style={styles.signBlock}>
            <View style={styles.signLine} />
            <Text style={styles.signLabel}>For {orgName}</Text>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerText}>
            This is a system-generated document. For verification, quote Case ID {app.caseId || app.applicationId || det.applicationId}.
          </Text>
        </View>
  </View>
      </Page>
    </Document>
  );
};

export default ProfessionalKYPDocument;
